import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import i18n from '../i18n/config';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { SignInWithApple, SignInWithAppleOptions, SignInWithAppleResponse } from '@capacitor-community/apple-sign-in';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  phoneVerified: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signInWithGoogle: () => Promise<{ error: any | null }>;
  signInWithApple: () => Promise<{ error: any | null }>;
  signInWithPhone: (phone: string) => Promise<{ error: any | null }>;
  signInWithPhonePassword: (phone: string, password: string) => Promise<{ error: any | null }>;
  verifyOtp: (phone: string, token: string) => Promise<{ error: any | null }>;
  signUp: (email: string, password: string, metadata?: { [key: string]: any }) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  checkPhoneVerification: () => Promise<boolean>;
  setPhoneVerified: (value: boolean) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const { toast } = useToast();

  const checkPhoneVerification = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      // Check user_profiles table as source of truth (more reliable than auth.users metadata)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('phone_verified')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking phone verification:', error);
        // Fallback to user metadata if database check fails
        const isPhoneVerified = user.user_metadata?.phone_verified === true;
        setPhoneVerified(isPhoneVerified);
        return isPhoneVerified;
      }

      const isPhoneVerified = data?.phone_verified === true;
      setPhoneVerified(isPhoneVerified);
      return isPhoneVerified;
    } catch (err) {
      console.error('Error in checkPhoneVerification:', err);
      // Fallback to user metadata
      const isPhoneVerified = user.user_metadata?.phone_verified === true;
      setPhoneVerified(isPhoneVerified);
      return isPhoneVerified;
    }
  };

  const ensureUserProfile = async (userId: string, userData: { name?: string, email: string, phone?: string }) => {
    try {
      console.log("Ensuring user profile exists for:", userId);
      
      const localProfile = localStorage.getItem(`userProfile-${userId}`);
      let existingLocalProfile = null;
      
      if (localProfile) {
        try {
          existingLocalProfile = JSON.parse(localProfile);
          console.log("Found local profile:", existingLocalProfile);
        } catch (e) {
          console.error("Error parsing local profile:", e);
        }
      }
      
      if (existingLocalProfile) {
        const updatedLocalProfile = {
          ...existingLocalProfile,
          name: userData.name || existingLocalProfile.name || userData.email,
          email: userData.email || existingLocalProfile.email,
          phone: userData.phone || existingLocalProfile.phone,
          updated_at: new Date().toISOString()
        };
        
        localStorage.setItem(`userProfile-${userId}`, JSON.stringify(updatedLocalProfile));
      } else {
        const newLocalProfile = {
          id: userId,
          name: userData.name || userData.email,
          email: userData.email,
          phone: userData.phone,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        localStorage.setItem(`userProfile-${userId}`, JSON.stringify(newLocalProfile));
        console.log("New profile saved to localStorage");
      }
      
      try {
        const { data: existingProfile, error: checkError } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('id', userId)
          .maybeSingle();
          
        if (checkError) {
          console.log("Error checking profile, proceeding with localStorage only:", checkError);
          return;
        }
        
        if (existingProfile) {
          const { error: updateError } = await supabase
            .from('user_profiles')
            .update({
              name: userData.name || userData.email,
              email: userData.email,
              phone: userData.phone,
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);
            
          if (updateError) {
            console.log("Profile update failed, continuing with localStorage:", updateError);
          } else {
            console.log("Profile updated in database successfully");
          }
        } else {
          const { error: insertError } = await supabase
            .from('user_profiles')
            .insert({
              id: userId,
              name: userData.name || userData.email,
              email: userData.email,
              phone: userData.phone
            });
            
          if (insertError) {
            console.log("Profile creation failed, continuing with localStorage:", insertError);
          } else {
            console.log("Profile created in database successfully");
          }
        }
      } catch (dbError) {
        console.error("Error ensuring user profile in database:", dbError);
        console.log("Continuing with localStorage profile only");
      }
      
      console.log("User profile ensured successfully");
    } catch (error) {
      console.error("Error in profile management:", error);
      const fallbackProfile = {
        id: userId,
        name: userData.name || userData.email,
        email: userData.email,
        phone: userData.phone,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      localStorage.setItem(`userProfile-${userId}`, JSON.stringify(fallbackProfile));
      console.log("Saved profile to localStorage as fallback");
    }
  };

  const handleOAuthRedirect = async () => {
    if (window.location.hash && window.location.hash.includes('access_token')) {
      try {
        console.log("Detected OAuth redirect with hash, attempting to process");
        
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error processing OAuth redirect:", error);
          throw error;
        }
        
        if (data?.session) {
          console.log("Successfully processed OAuth redirect session");
          
          window.history.replaceState({}, document.title, window.location.pathname);
          
          return data.session;
        } else {
          console.log("No session found after OAuth redirect");
        }
      } catch (error) {
        console.error("Error handling OAuth redirect:", error);
        toast({
          title: i18n.t('auth.oauthError'),
          description: i18n.t('auth.oauthErrorDescription'),
          variant: "destructive",
        });
      }
    }
    return null;
  };

  const initAuth = async () => {
    try {
      const oauthSession = await handleOAuthRedirect();
      
      if (oauthSession) {
        setSession(oauthSession);
        setUser(oauthSession.user);
        
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userId', oauthSession.user.id);
        
        setTimeout(async () => {
          await ensureUserProfile(
            oauthSession.user.id, 
            {
              name: oauthSession.user.user_metadata?.name,
              email: oauthSession.user.email || '',
              phone: oauthSession.user.user_metadata?.phone
            }
          );
          
          await checkPhoneVerification();
        }, 100);
      } else {
        const { data } = await supabase.auth.getSession();
        
        if (data?.session) {
          setSession(data.session);
          setUser(data.session.user);
          
          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('userId', data.session.user.id);
          
          setTimeout(async () => {
            await ensureUserProfile(
              data.session.user.id, 
              {
                name: data.session.user.user_metadata?.name,
                email: data.session.user.email || '',
                phone: data.session.user.user_metadata?.phone
              }
            );
            
            await checkPhoneVerification();
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error initializing auth:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle deep link OAuth callback for native apps
  const handleDeepLinkCallback = async (url: string) => {
    console.log('Deep link received:', url);

    // Check if this is an OAuth callback
    if (url.includes('auth/callback') || url.includes('access_token')) {
      try {
        // Close the in-app browser
        await Browser.close();

        // Extract the fragment/hash from the URL
        const urlObj = new URL(url);
        const hash = urlObj.hash || '';
        const params = new URLSearchParams(hash.replace('#', ''));

        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (accessToken && refreshToken) {
          console.log('Found OAuth tokens in deep link, setting session...');

          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('Error setting session from deep link:', error);
            toast({
              title: i18n.t('auth.oauthError'),
              description: error.message,
              variant: "destructive",
            });
          } else if (data?.session) {
            console.log('Session set successfully from deep link');
            setSession(data.session);
            setUser(data.session.user);

            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('userId', data.session.user.id);

            await ensureUserProfile(
              data.session.user.id,
              {
                name: data.session.user.user_metadata?.name,
                email: data.session.user.email || '',
                phone: data.session.user.user_metadata?.phone
              }
            );

            // Navigate to dashboard
            window.location.href = '/dashboard';
          }
        }
      } catch (error) {
        console.error('Error handling deep link OAuth callback:', error);
      }
    }
  };

  useEffect(() => {
    initAuth();

    // Set up deep link listener for native OAuth callbacks
    let deepLinkListener: any = null;
    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        console.log('App URL opened:', event.url);
        handleDeepLinkCallback(event.url);
      }).then(listener => {
        deepLinkListener = listener;
      });
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth state changed:', event);

        if ((currentSession?.user?.id !== user?.id) || event === 'SIGNED_OUT') {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);

          if (currentSession?.user) {
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('userId', currentSession.user.id);

            setTimeout(async () => {
              await ensureUserProfile(
                currentSession.user.id,
                {
                  name: currentSession.user.user_metadata?.name,
                  email: currentSession.user.email || '',
                  phone: currentSession.user.user_metadata?.phone
                }
              );

              await checkPhoneVerification();
            }, 100);
          } else {
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('userId');
            setPhoneVerified(false);
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if (deepLinkListener) {
        deepLinkListener.remove();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error, data } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      if (data?.user) {
        await ensureUserProfile(
          data.user.id, 
          {
            name: data.user.user_metadata?.name,
            email: data.user.email || '',
            phone: data.user.user_metadata?.phone
          }
        );
      }
      
      localStorage.setItem('isLoggedIn', 'true');
      return { error: null };
    } catch (error) {
      console.error('Error signing in:', error);
      toast({
        title: i18n.t('auth.loginFailed'),
        description: error.message || i18n.t('auth.loginFailedDescription'),
        variant: "destructive",
      });
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const isNative = Capacitor.isNativePlatform();
      const platform = Capacitor.getPlatform();

      if (isNative) {
        // Use native Google Sign-In on iOS/Android
        console.log('Using native Google Sign-In on platform:', platform);

        // Use platform-specific client ID for native OAuth flow
        // iOS needs the iOS client ID for URL scheme callbacks
        // Android uses the Android client ID
        const clientId = platform === 'ios'
          ? '327669478637-q0t8lef3oaidgus7lgpspj6qa4hvjeb8.apps.googleusercontent.com'
          : '327669478637-70luksp6q6d6s0e75t9vmb6nsr0qk9cd.apps.googleusercontent.com';

        // Initialize with platform client ID
        // serverClientId from capacitor.config.json is used for ID token audience
        // Don't use grantOfflineAccess - it adds a nonce that causes issues with Supabase
        await GoogleAuth.initialize({
          clientId: clientId,
          scopes: ['profile', 'email'],
          grantOfflineAccess: false,
        });
        console.log('GoogleAuth initialized with platform clientId:', clientId);

        const googleUser = await GoogleAuth.signIn();
        console.log('Google user:', JSON.stringify(googleUser, null, 2));

        if (googleUser?.authentication?.idToken) {
          // Use the ID token with Supabase
          // Pass access_token to help Supabase validate the token
          // (the ID token contains a nonce from Google, accessToken helps bypass nonce check)
          console.log('Calling signInWithIdToken...');
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: googleUser.authentication.idToken,
            access_token: googleUser.authentication.accessToken,
          });

          if (error) {
            console.error('Supabase signInWithIdToken error:', JSON.stringify(error));
            throw error;
          }

          if (data?.user) {
            await ensureUserProfile(data.user.id, {
              name: googleUser.name || googleUser.givenName || data.user.email || '',
              email: data.user.email || '',
              phone: data.user.user_metadata?.phone
            });
          }

          localStorage.setItem('isLoggedIn', 'true');
          const returnPath = localStorage.getItem('oauth_return_path') || '/dashboard';
          localStorage.removeItem('oauth_return_path');
          window.location.href = returnPath;
          return { error: null };
        } else {
          throw new Error('No ID token received from Google');
        }
      }

      // Web only - use OAuth redirect flow
      console.log('Using web OAuth flow');

      // Save current path before OAuth redirect
      localStorage.setItem('oauth_return_path', window.location.pathname);

      // Web OAuth - redirect back to current page
      const redirectTo = window.location.origin + window.location.pathname;
      console.log('Google OAuth redirect URL:', redirectTo);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
        },
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      toast({
        title: i18n.t('auth.googleLoginFailed'),
        description: error.message || i18n.t('auth.googleLoginFailedDescription'),
        variant: "destructive",
      });
      return { error };
    }
  };

  const signInWithApple = async () => {
    try {
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        // Use native Sign in with Apple on iOS
        console.log('Using native Sign in with Apple');

        const options: SignInWithAppleOptions = {
          clientId: 'com.ofair.consumer',
          redirectURI: 'https://erlfsougrkzbgonumhoa.supabase.co/auth/v1/callback',
          scopes: 'email name',
        };

        const result: SignInWithAppleResponse = await SignInWithApple.authorize(options);
        console.log('Apple Sign-In result:', result);

        if (result?.response?.identityToken) {
          // Use the ID token with Supabase
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: 'apple',
            token: result.response.identityToken,
          });

          if (error) throw error;

          if (data?.user) {
            const fullName = result.response.givenName && result.response.familyName
              ? `${result.response.givenName} ${result.response.familyName}`
              : result.response.givenName || result.response.familyName || '';

            await ensureUserProfile(data.user.id, {
              name: fullName || data.user.email || '',
              email: result.response.email || data.user.email || '',
              phone: data.user.user_metadata?.phone
            });
          }

          localStorage.setItem('isLoggedIn', 'true');
          window.location.href = '/dashboard';
          return { error: null };
        } else {
          throw new Error('No identity token received from Apple');
        }
      } else {
        // Web OAuth for Apple (opens browser)
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            redirectTo: window.location.origin + '/dashboard',
          },
        });

        if (error) throw error;
        return { error: null };
      }
    } catch (error: any) {
      console.error('Error signing in with Apple:', error);

      // User cancelled is not an error we need to show
      if (error.message?.includes('cancelled') || error.message?.includes('canceled')) {
        return { error: null };
      }

      toast({
        title: i18n.t('auth.appleLoginFailed') || 'Apple Sign-In Failed',
        description: error.message || i18n.t('auth.appleLoginFailedDescription') || 'Failed to sign in with Apple',
        variant: "destructive",
      });
      return { error };
    }
  };

  const signInWithPhone = async (phone: string) => {
    try {
      // Call the send-otp-consumer Edge Function
      const { data, error } = await supabase.functions.invoke('send-otp-consumer', {
        body: { phone }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return { error: null };
    } catch (error) {
      console.error('Error signing in with phone:', error);
      toast({
        title: i18n.t('auth.sendCodeFailed'),
        description: error.message || i18n.t('auth.sendCodeFailedDescription'),
        variant: "destructive",
      });
      return { error };
    }
  };

  const signInWithPhonePassword = async (phone: string, password: string) => {
    try {
      // Normalize phone number
      const normalizedPhone = phone.replace(/[-\s]/g, '');
      
      // Look up email by phone number
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('phone', normalizedPhone)
        .maybeSingle();

      if (profileError) {
        throw new Error(i18n.t('auth.phoneNotFound'));
      }

      if (!profileData?.email) {
        throw new Error(i18n.t('auth.phoneNotFound'));
      }

      // Sign in with the found email
      const { error, data } = await supabase.auth.signInWithPassword({ 
        email: profileData.email, 
        password 
      });
      
      if (error) throw error;
      
      if (data?.user) {
        await ensureUserProfile(
          data.user.id, 
          {
            name: data.user.user_metadata?.name,
            email: data.user.email || '',
            phone: data.user.user_metadata?.phone
          }
        );
      }
      
      localStorage.setItem('isLoggedIn', 'true');
      return { error: null };
    } catch (error: any) {
      console.error('Error signing in with phone:', error);
      toast({
        title: i18n.t('auth.loginFailed'),
        description: error.message || i18n.t('auth.loginFailedDescription'),
        variant: "destructive",
      });
      return { error };
    }
  };

  const verifyOtp = async (phone: string, token: string) => {
    try {
      // Call the verify-otp-consumer Edge Function
      const { data, error } = await supabase.functions.invoke('verify-otp-consumer', {
        body: {
          phone,
          otp: token,
          userId: user?.id
        }
      });

      // Check for phone already registered error in various places
      const checkPhoneAlreadyRegistered = (msg: string | undefined | null) => {
        return msg && (
          msg.includes('Phone number already registered') || 
          msg.includes('phone already registered')
        );
      };

      // Handle edge function errors (400 responses will have error set)
      if (error) {
        // Try to parse error context if available
        const errorContext = error.context;
        let errorMsg = error.message || '';
        
        // Try to extract JSON from error message if it contains JSON
        try {
          const jsonMatch = errorMsg.match(/\{.*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            if (parsed.error) errorMsg = parsed.error;
          }
        } catch (e) {
          // Ignore JSON parsing errors
        }

        if (checkPhoneAlreadyRegistered(errorMsg)) {
          throw new Error(i18n.t('auth.phoneAlreadyRegistered'));
        }
        throw new Error(errorMsg || i18n.t('auth.verifyCodeFailedDescription'));
      }
      
      // Handle verification failure from response data
      if (!data?.verified) {
        const errorMsg = data?.error || 'Invalid verification code';
        if (checkPhoneAlreadyRegistered(errorMsg)) {
          throw new Error(i18n.t('auth.phoneAlreadyRegistered'));
        }
        throw new Error(errorMsg);
      }

      // Update user metadata if logged in
      if (user) {
        const { error: updateError } = await supabase.auth.updateUser({
          data: { phone }
        });

        if (updateError) {
          console.error('Error updating user metadata:', updateError);
        } else {
          setPhoneVerified(true);

          if (user.id) {
            await ensureUserProfile(
              user.id,
              {
                name: user.user_metadata?.name,
                email: user.email || '',
                phone
              }
            );
          }
        }
      }

      localStorage.setItem('isLoggedIn', 'true');
      return { error: null };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast({
        title: i18n.t('auth.verifyCodeFailed'),
        description: error.message || i18n.t('auth.verifyCodeFailedDescription'),
        variant: "destructive",
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string, metadata?: { [key: string]: any }) => {
    try {
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: window.location.origin + '/login',
        },
      });
      if (error) throw error;
      
      if (data?.user) {
        await ensureUserProfile(
          data.user.id, 
          {
            name: metadata?.name || email,
            email: email,
            phone: metadata?.phone
          }
        );
      }
      
      localStorage.setItem('isLoggedIn', 'true');
      return { error: null };
    } catch (error: any) {
      console.error('Error signing up:', error);
      
      // Translate common Supabase error messages
      let errorDescription = i18n.t('auth.registrationFailedDescription');
      const errorMessage = error.message || '';
      
      if (errorMessage.includes('User already registered')) {
        errorDescription = i18n.t('auth.userAlreadyRegistered');
      } else if (errorMessage.includes('Password should be')) {
        errorDescription = i18n.t('auth.weakPasswordDescription');
      } else if (errorMessage.includes('Invalid email')) {
        errorDescription = i18n.t('auth.invalidEmailDescription');
      } else if (errorMessage) {
        errorDescription = errorMessage;
      }
      
      toast({
        title: i18n.t('auth.registrationFailed'),
        description: errorDescription,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      console.log("Attempting to sign out...");
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Error during sign out:", error);
        throw error;
      }
      
      console.log("Sign out successful");
      localStorage.removeItem('isLoggedIn');
      setPhoneVerified(false);
      
      setUser(null);
      setSession(null);

      toast({
        title: i18n.t('auth.loggedOut'),
        description: i18n.t('auth.loggedOutDescription'),
      });

      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: i18n.t('auth.logoutFailed'),
        description: i18n.t('auth.logoutFailedDescription'),
        variant: "destructive",
      });
    }
  };

  const value = {
    session,
    user,
    loading,
    phoneVerified,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signInWithPhone,
    signInWithPhonePassword,
    verifyOtp,
    signUp,
    signOut,
    checkPhoneVerification,
    setPhoneVerified,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
